import { useState, useEffect } from 'react'
import { Link } from 'react-router'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import { useSessionStore } from '../../state'
import {
  submitRequest,
  getUserRequestForDive
} from '../../services/joinRequestService'
import Database from '../../types/Database'

export type JoinRequestButtonProps = {
  diveId: string
  diveStatus: string
  hostId: string
  diveType: 'buddy' | 'guided'
}

const JoinRequestButton = ({
  diveId,
  diveStatus,
  hostId,
  diveType
}: JoinRequestButtonProps) => {
  const user = useSessionStore(state => state.user)
  const profile = useSessionStore(state => state.profile)

  const [isLoading, setIsLoading] = useState(false)
  const [isCheckingExisting, setIsCheckingExisting] = useState(true)
  const [existingRequest, setExistingRequest] =
    useState<Database.JoinRequest | null>(null)
  const [feedback, setFeedback] = useState<{
    type: 'success' | 'error'
    message: string
  } | null>(null)

  // Check for existing request on mount
  useEffect(() => {
    let cancelled = false

    const checkExisting = async () => {
      if (!user) {
        setIsCheckingExisting(false)
        return
      }

      try {
        const request = await getUserRequestForDive(diveId)
        if (!cancelled) {
          setExistingRequest(request)
        }
      } catch {
        // Silently fail — button will be enabled
      } finally {
        if (!cancelled) {
          setIsCheckingExisting(false)
        }
      }
    }

    checkExisting()
    return () => {
      cancelled = true
    }
  }, [diveId, user])

  if (!user) {
    return null
  }

  // Determine disabled state and tooltip
  const isHost = user.uid === hostId
  const isDiveOpen = diveStatus === 'open'
  const hasExistingRequest =
    existingRequest?.status === 'pending' ||
    existingRequest?.status === 'approved'
  const isVerified = profile?.accreditation?.status === 'verified'

  let disabled = false
  let tooltipText = ''

  if (isHost) {
    disabled = true
    tooltipText = 'You cannot join your own dive'
  } else if (!isDiveOpen) {
    disabled = true
    tooltipText = 'This dive is not accepting requests'
  } else if (hasExistingRequest) {
    disabled = true
    tooltipText = 'Already requested'
  } else if (!isVerified) {
    disabled = true
    tooltipText = 'Verification required'
  }

  const handleClick = async () => {
    setIsLoading(true)
    setFeedback(null)

    try {
      const result = await submitRequest(diveId)

      if ('error' in result) {
        setFeedback({ type: 'error', message: result.error })
      } else if (result.status === 'auto-approved') {
        setFeedback({ type: 'success', message: 'Auto-approved!' })
        setExistingRequest({
          id: '',
          diveId,
          requesterId: user.uid,
          status: 'approved',
          paymentStatus: diveType === 'guided' ? 'awaiting' : null,
          paymentDeadline: null,
          testData: false,
          createdAt: null as unknown as Database.JoinRequest['createdAt']
        })
      } else {
        setFeedback({ type: 'success', message: 'Request sent' })
        setExistingRequest({
          id: '',
          diveId,
          requesterId: user.uid,
          status: 'pending',
          paymentStatus: null,
          paymentDeadline: null,
          testData: false,
          createdAt: null as unknown as Database.JoinRequest['createdAt']
        })
      }
    } catch {
      setFeedback({
        type: 'error',
        message: 'Failed to submit request. Please try again.'
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (isCheckingExisting) {
    return (
      <Button variant='contained' disabled>
        <CircularProgress size={18} sx={{ mr: 1 }} />
        Loading...
      </Button>
    )
  }

  // Show payment status UI for approved guided dive requests
  if (existingRequest?.status === 'approved' && existingRequest.paymentStatus) {
    if (existingRequest.paymentStatus === 'awaiting') {
      return (
        <Stack spacing={1}>
          <Button
            component={Link}
            to={`/payment/${existingRequest.id}`}
            variant='contained'
            color='primary'
          >
            Complete Payment
          </Button>
        </Stack>
      )
    }

    if (existingRequest.paymentStatus === 'expired') {
      return (
        <Stack spacing={1}>
          <Chip label='Payment expired' color='error' variant='outlined' />
        </Stack>
      )
    }

    if (existingRequest.paymentStatus === 'paid') {
      return (
        <Stack spacing={1}>
          <Chip label='Paid' color='success' variant='outlined' />
        </Stack>
      )
    }
  }

  const button = (
    <Button
      variant='contained'
      onClick={handleClick}
      disabled={disabled || isLoading}
      startIcon={isLoading ? <CircularProgress size={18} /> : undefined}
    >
      {isLoading ? 'Sending...' : 'Request to join'}
    </Button>
  )

  return (
    <Stack spacing={1}>
      {tooltipText ? (
        <Tooltip title={tooltipText}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      {feedback && <Alert severity={feedback.type}>{feedback.message}</Alert>}
    </Stack>
  )
}

export default JoinRequestButton
